import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { SkillNode, SkillLink } from '@skill-trees/shared';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { undoRedoModifierLabel } from '../tree-domain/useUndoRedoShortcuts';
import { NodeSettingsInteractor } from './NodeSettingsInteractor';
import { ApplyIcon } from '../ui/icons';

interface NodeSettingsPanelProps {
    node: SkillNode | null;
    onRename: (nodeId: string, newName: string) => void;
    onDelete: (nodeId: string) => void;
    onDeleteLink: (sourceId: string, targetId: string) => void;
    onClose?: () => void;
    allNodes: SkillNode[];
    allLinks: SkillLink[];
    /** When false, "+ New Guide" is shown (dev editor). When true, guide row with remove control. */
    leafHasLinkedGuide?: boolean;
    onAddGuide?: () => void;
    /** Remove persisted guide after confirmation (same flow as skill panel delete guide). */
    onDeleteGuide?: () => Promise<boolean>;
}

export const NodeSettingsPanel: React.FC<NodeSettingsPanelProps> = ({ 
    node, 
    onRename, 
    onDelete, 
    onDeleteLink,
    onClose,
    allNodes,
    allLinks,
    leafHasLinkedGuide = false,
    onAddGuide,
    onDeleteGuide,
}) => {
    const [name, setName] = useState<string>(node?.name ?? '');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeleteGuideModal, setShowDeleteGuideModal] = useState(false);
    // Latest onRename for the interactor's commits; a ref so the interactor is built once.
    const onRenameRef = useRef(onRename);
    useEffect(() => {
        onRenameRef.current = onRename;
    }, [onRename]);

    // Rename-commit policy (debounce, apply, flush-on-unmount) lives in the interactor.
    const settingsInteractor = useMemo(
        () => new NodeSettingsInteractor((nodeId, newName) => onRenameRef.current(nodeId, newName)),
        []
    );

    useEffect(() => {
        setName(node?.name ?? '');
    }, [node?.id]);

    // Deselecting the node, collapsing the panel, or leaving edit mode unmounts this
    // component mid-debounce; flush so the typed name still applies.
    useEffect(() => () => settingsInteractor.flushPendingRename(), [settingsInteractor]);

    const applyRename = useCallback(() => {
        if (!node) return;
        settingsInteractor.applyRename(node.id, name);
    }, [node, name, settingsInteractor]);

    if (!node) return null;

    // Calculate parent and child skills
    const parentSkills = allLinks
        .filter(link => link.target === node.id)
        .map(link => allNodes.find(n => n.id === link.source))
        .filter(Boolean) as SkillNode[];

    const childSkills = allLinks
        .filter(link => link.source === node.id)
        .map(link => allNodes.find(n => n.id === link.target))
        .filter(Boolean) as SkillNode[];

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setName(newValue);
        settingsInteractor.scheduleRename(node.id, newValue);
    };

    const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            applyRename();
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = () => {
        onDelete(node.id);
        setShowDeleteModal(false);
        if (onClose) onClose();
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
    };

    const handleDeleteParentLink = (parentId: string) => {
        onDeleteLink(parentId, node.id);
    };

    const handleDeleteChildLink = (childId: string) => {
        onDeleteLink(node.id, childId);
    };

    const handleDeleteGuideRowClick = () => {
        setShowDeleteGuideModal(true);
    };

    const handleDeleteGuideConfirm = async () => {
        if (!onDeleteGuide) {
            setShowDeleteGuideModal(false);
            return;
        }
        const ok = await onDeleteGuide();
        if (ok) {
            setShowDeleteGuideModal(false);
        }
    };

    const handleDeleteGuideCancel = () => {
        setShowDeleteGuideModal(false);
    };

    const showGuideSection = onAddGuide !== undefined;

    return (
        <div className="node-settings-panel">
            <div className="node-settings-panel-header">
                <h4>Skill leaf settings</h4>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="btn btn-primary btn-small"
                    >
                        Close
                    </button>
                )}
            </div>
            
            <div className="node-settings-panel-content">
                {/* Name Field */}
                <div className="form-group">
                    <label className="form-label">Name</label>
                    <div className="form-input-row">
                        <input
                            type="text"
                            value={name}
                            onChange={handleNameChange}
                            onKeyDown={handleNameKeyDown}
                            placeholder="Skill leaf name"
                            className="form-input"
                        />
                        <button
                            type="button"
                            onClick={applyRename}
                            className="btn btn-secondary btn-icon"
                            aria-label="Apply name"
                            title="Apply name"
                        >
                            <ApplyIcon />
                        </button>
                    </div>
                </div>

                {showGuideSection && (
                    <div className="form-group">
                        <label className="form-label">Skill leaf guide</label>
                        <div className="list-container">
                            {leafHasLinkedGuide ? (
                                <div className="list-item">
                                    <span className="list-item-text">Skill guide (markdown)</span>
                                    {onDeleteGuide ? (
                                        <button
                                            type="button"
                                            onClick={handleDeleteGuideRowClick}
                                            className="btn-x"
                                            aria-label="Remove skill guide"
                                        >
                                            ×
                                        </button>
                                    ) : null}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onAddGuide}
                                    className="btn btn-secondary"
                                    style={{ width: '100%' }}
                                >
                                    + New Guide
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Parent skill leaves (prerequisites) */}
                <div className="form-group">
                    <label className="form-label">Parent skill leaves</label>
                    <div className="list-container">
                        {parentSkills.length === 0 ? (
                            <div className="empty-list">
                                No parent skill leaves
                            </div>
                        ) : (
                            parentSkills.map(parent => (
                                <div key={parent.id} className="list-item">
                                    <span className="list-item-text">{parent.name}</span>
                                    <button
                                        onClick={() => handleDeleteParentLink(parent.id)}
                                        className="btn-x"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Child skill leaves (follow-ups) */}
                <div className="form-group">
                    <label className="form-label">Child skill leaves</label>
                    <div className="list-container">
                        {childSkills.length === 0 ? (
                            <div className="empty-list">
                                No child skill leaves
                            </div>
                        ) : (
                            childSkills.map(child => (
                                <div key={child.id} className="list-item">
                                    <span className="list-item-text">{child.name}</span>
                                    <button
                                        onClick={() => handleDeleteChildLink(child.id)}
                                        className="btn-x"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Delete Button */}
                <div className="section-divider">
                    <button
                        onClick={handleDeleteClick}
                        className="btn btn-danger"
                        style={{ width: '100%' }}
                    >
                        Delete Node
                    </button>
                </div>

            </div>
            
            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                title="Delete skill leaf"
                message={`Are you sure you want to delete "${node.name}"? This will also remove all links connected to this skill leaf. You can undo this with ${undoRedoModifierLabel()}+Z.`}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
            <ConfirmationModal
                isOpen={showDeleteGuideModal}
                title="Delete guide"
                message="Remove the markdown guide for this skill leaf? This deletes the guide file and cannot be undone."
                confirmText="Delete guide"
                cancelText="Cancel"
                onConfirm={() => {
                    void handleDeleteGuideConfirm();
                }}
                onCancel={handleDeleteGuideCancel}
            />
        </div>
    );
}
